import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, CustomSelect, DateTimePicker } from '@/components';
import { parseApiError } from '@/common/utils';
import { getCompanyEmployees } from '../../api';
import {
  STATUS_META,
  formatMinutesLabel,
  formatScheduledAtDate,
  formatScheduledAtTime,
  getAppointmentScheduleFields,
  readAnyContactName,
  timeToMinutes,
  dayKeyToUtcDate,
} from '../utils';
import {
  useAppointmentDetail,
  useUpdateAppointment,
  useCreateAppointmentTask,
  useCompleteAppointmentTask,
  useCreateAppointmentNote,
} from '../hooks';
import { MeetDetail } from './MeetLinkBlock';
import {
  ClockIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  BoltIcon,
  PhoneIcon,
  MailIcon,
  NotesIcon,
  TaskIcon,
  AuthorIcon,
} from './icons';

function formatScheduleLine(appointment, schedule, event) {
  const scheduledAt = appointment?.scheduled_at;
  if (scheduledAt) {
    const dateLabel = formatScheduledAtDate(scheduledAt);
    const startLabel = formatScheduledAtTime(scheduledAt);
    const endMinutes =
      timeToMinutes(schedule.end_time) ??
      event?.endMinutes ??
      (timeToMinutes(schedule.start_time) ?? 0) + 30;
    const startMinutes =
      timeToMinutes(schedule.start_time) ?? event?.startMinutes ?? 0;
    const endLabel = formatMinutesLabel(endMinutes);
    const startFallback = formatMinutesLabel(startMinutes);
    return `${dateLabel} • ${startLabel || startFallback} — ${endLabel}`;
  }

  const date = schedule.date || event?.dayKey || '';
  const startMinutes =
    timeToMinutes(schedule.start_time) ?? event?.startMinutes ?? 0;
  const endMinutes =
    timeToMinutes(schedule.end_time) ?? event?.endMinutes ?? startMinutes + 30;
  const d = dayKeyToUtcDate(date);
  const dateLabel = d
    ? new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(d)
    : date;
  return `${dateLabel} • ${formatMinutesLabel(startMinutes)} — ${formatMinutesLabel(endMinutes)}`;
}

function readNotes(appointment) {
  const value =
    appointment?.note ||
    appointment?.description ||
    appointment?.remarks ||
    appointment?.comment;
  return typeof value === 'string' ? value.trim() : '';
}

/** Format an ISO date string to a readable short date/time label. */
function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(isoString));
  } catch {
    return isoString;
  }
}

/** Tasks section — rendered inside the detail popover. */
function AppointmentTasksSection({
  tasks,
  companyId,
  contactId,
  appointmentId,
  canAdd = true,
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDueAt, setTaskDueAt] = useState(null);
  const [assignedToId, setAssignedToId] = useState('');

  const { data: employeesData } = useQuery({
    queryKey: ['companyEmployees-select', companyId],
    queryFn: () => getCompanyEmployees(companyId, { page_size: 100 }),
    enabled: !!companyId && isAdding,
  });
  const employees =
    employeesData?.results ||
    (Array.isArray(employeesData) ? employeesData : []);

  const createTaskMutation = useCreateAppointmentTask(companyId, appointmentId);
  const completeTaskMutation = useCompleteAppointmentTask(
    companyId,
    appointmentId,
  );

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskTitle.trim() || !contactId) return;
    try {
      await createTaskMutation.mutateAsync({
        contactId,
        payload: {
          title: taskTitle.trim(),
          due_at: taskDueAt ? new Date(taskDueAt).toISOString() : null,
          assigned_to_employee_id: assignedToId ? Number(assignedToId) : null,
        },
      });
      toast.success('Task added successfully!');
      setIsAdding(false);
      setTaskTitle('');
      setTaskDueAt(null);
      setAssignedToId('');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add task.');
    }
  };

  const handleCompleteTask = async (taskId) => {
    if (!contactId) return;
    try {
      await completeTaskMutation.mutateAsync({ contactId, taskId });
      toast.success('Task completed!');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to complete task.');
    }
  };

  const sorted = Array.isArray(tasks)
    ? [...tasks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  return (
    <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 flex flex-col gap-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TaskIcon className="w-4 h-4 text-[#50AB3D]" />
          <h3 className="text-[16px] leading-[24px] font-medium text-[#50AB3D]">
            Tasks
          </h3>
        </div>
        {canAdd && contactId && (
          <button
            type="button"
            onClick={() => setIsAdding((prev) => !prev)}
            className="text-xs font-semibold text-[#50AB3D] hover:text-[#2F8E29] transition-colors flex items-center gap-1 cursor-pointer"
          >
            {isAdding ? 'Cancel' : '+ Add Task'}
          </button>
        )}
      </div>

      {isAdding && (
        <form
          onSubmit={handleCreateTask}
          className="bg-white rounded-[12px] p-3.5 flex flex-col gap-3 border border-[#EBF4EA] shadow-sm"
        >
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-[#868686]">
              Task Title *
            </label>
            <input
              type="text"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              placeholder="Enter task title..."
              className="w-full h-10 px-3 rounded-[10px] bg-white border border-[#EBF4EA] text-sm text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:border-[#50AB3D] focus:ring-1 focus:ring-[#50AB3D]/20 transition-all"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-1.5 min-w-0">
              <label className="text-xs font-medium text-[#868686]">
                Due Date
              </label>
              <DateTimePicker
                selected={taskDueAt}
                onChange={(date) => setTaskDueAt(date)}
                mode="datetime"
                placeholderText="Select due date"
                className="w-full h-10 px-3 rounded-xl bg-white border border-[#EBF4EA] text-xs font-medium text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:border-[#50AB3D] focus:ring-1 focus:ring-[#50AB3D]/20 transition-all cursor-pointer"
              />
            </div>

            <div className="flex flex-col gap-1.5 min-w-0">
              <label className="text-xs font-medium text-[#868686]">
                Assign To
              </label>
              <CustomSelect
                value={assignedToId}
                onChange={(val) => setAssignedToId(val)}
                options={[
                  { value: '', label: 'Unassigned' },
                  ...employees.map((emp) => ({
                    value: String(emp.id),
                    label: emp.name || emp.full_name,
                  })),
                ]}
                placeholder="Select employee"
                className="w-full h-10"
                textSizeClass="text-xs font-medium"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setTaskTitle('');
                setTaskDueAt(null);
                setAssignedToId('');
              }}
              className="h-8 px-3 rounded-[8px] text-xs font-medium text-[#868686] hover:bg-[#F5F5F5] transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!taskTitle.trim() || createTaskMutation.isPending}
              className="h-8 px-3 rounded-[8px] text-xs font-medium bg-[#50AB3D] text-white hover:bg-[#2F8E29] disabled:opacity-50 transition-colors cursor-pointer"
            >
              {createTaskMutation.isPending ? 'Adding...' : 'Add Task'}
            </button>
          </div>
        </form>
      )}

      {sorted.length === 0 && !isAdding ? (
        <div className="bg-white rounded-[12px] p-3.5 flex flex-col items-center justify-center gap-2 border border-[#EBF4EA]">
          <p className="text-sm text-[#868686] text-center">
            No tasks available
          </p>
          {canAdd && contactId && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="text-xs font-medium text-[#50AB3D] hover:text-[#2F8E29] transition-colors cursor-pointer"
            >
              + Add Task
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 max-h-[260px] sm:max-h-[300px] overflow-y-auto custom-scrollbar pr-1">
          {sorted.map((task) => {
            const isCompleted = Boolean(task.completed_at);
            return (
              <div
                key={task.id}
                className="bg-white rounded-[12px] p-3.5 flex flex-col gap-2 min-w-0 border border-[#EBF4EA]"
              >
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <span
                    className={`text-sm font-semibold break-words min-w-0 ${isCompleted ? 'line-through text-[#868686]' : 'text-[#1F2241]'}`}
                  >
                    {task.title}
                  </span>
                  <button
                    type="button"
                    disabled={isCompleted || completeTaskMutation.isPending}
                    onClick={() => handleCompleteTask(task.id)}
                    className={`inline-flex items-center shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                      isCompleted
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 cursor-default'
                        : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 cursor-pointer'
                    }`}
                    title={!isCompleted ? 'Click to mark completed' : undefined}
                  >
                    {isCompleted ? 'Completed' : 'Pending'}
                  </button>
                </div>
                <div className="flex flex-col gap-1.5">
                  {task.assigned_to_employee_name ? (
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-3.5 h-3.5 text-[#868686] shrink-0" />
                      <span className="text-xs text-[#868686]">
                        {task.assigned_to_employee_name}
                      </span>
                    </div>
                  ) : null}
                  {task.due_at ? (
                    <div className="flex items-center gap-2">
                      <ClockIcon className="w-3.5 h-3.5 text-[#868686] shrink-0" />
                      <span className="text-xs text-[#868686]">
                        Due {formatDate(task.due_at)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Notes section — rendered inside the detail popover. */
function AppointmentNotesSection({
  notes,
  companyId,
  contactId,
  appointmentId,
  canAdd = true,
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [noteBody, setNoteBody] = useState('');

  const createNoteMutation = useCreateAppointmentNote(companyId, appointmentId);

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!noteBody.trim() || !contactId) return;
    try {
      await createNoteMutation.mutateAsync({
        contactId,
        payload: {
          body: noteBody.trim(),
        },
      });
      toast.success('Note added successfully!');
      setIsAdding(false);
      setNoteBody('');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add note.');
    }
  };

  const sorted = Array.isArray(notes)
    ? [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  return (
    <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 flex flex-col gap-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <NotesIcon className="w-4 h-4 text-[#50AB3D]" />
          <h3 className="text-[16px] leading-[24px] font-medium text-[#50AB3D]">
            Notes
          </h3>
        </div>
        {canAdd && contactId && (
          <button
            type="button"
            onClick={() => setIsAdding((prev) => !prev)}
            className="text-xs font-semibold text-[#50AB3D] hover:text-[#2F8E29] transition-colors flex items-center gap-1 cursor-pointer"
          >
            {isAdding ? 'Cancel' : '+ Add Note'}
          </button>
        )}
      </div>

      {isAdding && (
        <form
          onSubmit={handleCreateNote}
          className="bg-white rounded-[12px] p-3.5 flex flex-col gap-3 border border-[#EBF4EA] shadow-sm"
        >
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-[#868686]">Note *</label>
            <textarea
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Enter note here..."
              rows={3}
              className="w-full px-3 py-2 rounded-[10px] bg-white border border-[#EBF4EA] text-sm text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:border-[#50AB3D] focus:ring-1 focus:ring-[#50AB3D]/20 resize-none transition-all"
              autoFocus
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setNoteBody('');
              }}
              className="h-8 px-3 rounded-[8px] text-xs font-medium text-[#868686] hover:bg-[#F5F5F5] transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!noteBody.trim() || createNoteMutation.isPending}
              className="h-8 px-3 rounded-[8px] text-xs font-medium bg-[#50AB3D] text-white hover:bg-[#2F8E29] disabled:opacity-50 transition-colors cursor-pointer"
            >
              {createNoteMutation.isPending ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </form>
      )}

      {sorted.length === 0 && !isAdding ? (
        <div className="bg-white rounded-[12px] p-3.5 flex flex-col items-center justify-center gap-2 border border-[#EBF4EA]">
          <p className="text-sm text-[#868686] text-center">
            No notes available
          </p>
          {canAdd && contactId && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="text-xs font-medium text-[#50AB3D] hover:text-[#2F8E29] transition-colors cursor-pointer"
            >
              + Add Note
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto custom-scrollbar pr-1">
          {sorted.map((note) => (
            <div
              key={note.id}
              className="bg-white rounded-[12px] p-3.5 flex flex-col gap-2 min-w-0 border border-[#EBF4EA]"
            >
              <p className="text-sm font-medium text-[#1F2241] whitespace-pre-wrap break-words">
                {note.body}
              </p>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {note.author_name ? (
                  <div className="flex items-center gap-1.5">
                    <AuthorIcon className="w-3.5 h-3.5 text-[#868686] shrink-0" />
                    <span className="text-xs text-[#868686]">
                      {note.author_name}
                    </span>
                  </div>
                ) : (
                  <span />
                )}
                {note.created_at ? (
                  <span className="text-xs text-[#868686]">
                    {formatDate(note.created_at)}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, children }) {
  if (!value && !children) return null;
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-[#868686] shrink-0" />}
        <span className="text-xs font-medium text-[#868686]">{label}</span>
      </div>
      {children || (
        <span className="text-sm font-semibold text-[#1F2241] break-words pl-6">
          {value}
        </span>
      )}
    </div>
  );
}

export function AppointmentPopover({
  companyId,
  event,
  canUpdate,
  canDelete,
  onClose,
  onEdit,
  onDelete,
  onContactClick,
  onBusyChange,
}) {
  const updateMutation = useUpdateAppointment(companyId);

  const appointmentId = event?.id || event?.raw?.id;
  const { data: detail } = useAppointmentDetail(
    { companyId, appointmentId },
    { enabled: !!companyId && !!appointmentId },
  );

  const appointment = detail || event?.raw || event;
  const schedule = getAppointmentScheduleFields(appointment);
  const status = appointment?.status || event?.status || 'scheduled';
  const meta = STATUS_META[status] || STATUS_META.scheduled;
  const contactName =
    readAnyContactName(appointment?.contact) ||
    readAnyContactName(appointment) ||
    appointment?.contact_name ||
    [appointment?.contact_first_name, appointment?.contact_last_name]
      .filter(Boolean)
      .join(' ') ||
    event?.contactName ||
    event?.title ||
    'Appointment';
  const assigneeName =
    appointment?.assigned_employee?.full_name ||
    appointment?.assigned_employee?.name ||
    appointment?.assigned_employee_name ||
    event?.assigneeName ||
    '—';
  const contactId =
    event?.contactId ||
    appointment?.contact_id ||
    appointment?.contact?.id ||
    event?.raw?.contact_id ||
    event?.raw?.contact?.id ||
    event?.contact_id ||
    event?.contact?.id ||
    null;
  const canOpenContact = Boolean(contactId && onContactClick);
  const phone =
    appointment?.contact_phone ||
    appointment?.contact?.phone_normalized ||
    appointment?.contact?.phone ||
    '';
  const email = appointment?.contact_email || appointment?.contact?.email || '';
  const notes = readNotes(appointment);
  // `tasks` and `notes` arrays come exclusively from the detail API response.
  const appointmentTasks = Array.isArray(detail?.tasks) ? detail.tasks : [];
  const appointmentNotes = Array.isArray(detail?.notes) ? detail.notes : [];

  useEffect(() => {
    onBusyChange?.(updateMutation.isPending);
    return () => onBusyChange?.(false);
  }, [updateMutation.isPending, onBusyChange]);

  const setStatus = async (nextStatus) => {
    if (!canUpdate || !appointmentId || nextStatus === status) return;
    try {
      await updateMutation.mutateAsync({
        appointmentId,
        payload: { status: nextStatus },
      });
      toast.success(
        `Marked as ${STATUS_META[nextStatus]?.label || nextStatus}`,
      );
      onClose?.();
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update status');
    }
  };

  if (!event) return null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 flex flex-col gap-4 bg-white min-w-0">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`w-2.5 h-2.5 rounded-full shrink-0 ${meta.barClass}`}
            />
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.chipClass}`}
            >
              {meta.label}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canUpdate && (
              <button
                type="button"
                onClick={() => onEdit?.(appointment)}
                className="w-8 h-8 rounded-lg text-[#868686] hover:bg-[#F5F5F5] hover:text-[#1F2241] flex items-center justify-center"
                aria-label="Edit"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete?.(appointment)}
                className="w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center"
                aria-label="Delete"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 flex flex-col gap-3 min-w-0">
          <h3 className="text-[16px] leading-[24px] font-medium text-[#50AB3D]">
            Appointment information
          </h3>
          <div className="bg-white rounded-[12px] p-3.5 flex flex-col gap-3.5 min-w-0">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-xs font-medium text-[#868686]">
                Contact / Lead
              </span>
              {canOpenContact ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContactClick(contactId, contactName);
                  }}
                  className="text-sm font-semibold text-[#1F2241] text-left break-words hover:text-[#50AB3D] hover:underline underline-offset-2 transition-colors"
                  title="View lead details"
                  aria-label={`Open ${contactName} details`}
                >
                  {contactName}
                </button>
              ) : (
                <span className="text-sm font-semibold text-[#1F2241] break-words">
                  {contactName}
                </span>
              )}
            </div>

            <DetailRow icon={PhoneIcon} label="Phone" value={phone || '—'} />
            {email ? (
              <DetailRow icon={MailIcon} label="Email" value={email} />
            ) : null}
            <DetailRow
              icon={ClockIcon}
              label="Schedule"
              value={formatScheduleLine(appointment, schedule, event)}
            />
            <MeetDetail
              appointment={appointment}
              status={status}
              companyId={companyId}
              contactPhone={phone}
            />
            <DetailRow
              icon={UserIcon}
              label="Assigned user"
              value={assigneeName}
            />
            {appointment?.flow_name ? (
              <DetailRow
                icon={BoltIcon}
                label="Flow"
                value={appointment.flow_name}
              />
            ) : null}
            {notes ? (
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2">
                  <NotesIcon className="w-4 h-4 text-[#868686] shrink-0" />
                  <span className="text-xs font-medium text-[#868686]">
                    Notes
                  </span>
                </div>
                <p className="text-sm font-medium text-[#1F2241] whitespace-pre-wrap break-words pl-6">
                  {notes}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <AppointmentTasksSection
          tasks={appointmentTasks}
          companyId={companyId}
          contactId={contactId}
          appointmentId={appointmentId}
          canAdd={canUpdate}
        />
        <AppointmentNotesSection
          notes={appointmentNotes}
          companyId={companyId}
          contactId={contactId}
          appointmentId={appointmentId}
          canAdd={canUpdate}
        />
      </div>

      {canUpdate && (
        <div className="flex items-center px-4 py-4 border-t border-[#EBEBEB] shrink-0 gap-3 bg-white">
          {status !== 'cancelled' && (
            <Button
              type="button"
              variant="secondary"
              disabled={updateMutation.isPending}
              onClick={() => setStatus('cancelled')}
              className="flex-1 h-[44px] px-3 sm:px-4 text-[13px] sm:text-[14px] font-medium rounded-[8px] whitespace-nowrap"
            >
              Cancel appointment
            </Button>
          )}
          {status !== 'completed' && (
            <Button
              type="button"
              variant="primary"
              disabled={updateMutation.isPending}
              onClick={() => setStatus('completed')}
              className="flex-1 h-[44px] px-3 sm:px-4 text-[13px] sm:text-[14px] font-medium rounded-[8px] whitespace-nowrap"
            >
              Mark completed
            </Button>
          )}
          {status !== 'scheduled' && (
            <Button
              type="button"
              variant="secondary"
              disabled={updateMutation.isPending}
              onClick={() => setStatus('scheduled')}
              className="flex-1 h-[44px] px-3 sm:px-4 text-[13px] sm:text-[14px] font-medium rounded-[8px] whitespace-nowrap"
            >
              Reopen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
